"use client"

import {
  ArrowRight,
  CalendarClock,
  Check,
  HardHat,
  Info,
  Layers,
  Package,
  Sprout,
  Zap,
} from "lucide-react"
import { formatSodUnit, type SodResult, type SodUnit } from "@/lib/sod/calculator"

interface SodResultsProps {
  result: SodResult
  unit: SodUnit
  onUnitChange: (unit: SodUnit) => void
  ctaHref?: string
  onCtaClick?: () => void
  /** Ref for the CTA anchor — lets the page attach its impression observer. */
  ctaRef?: React.Ref<HTMLAnchorElement>
}

const sectionLabel: React.CSSProperties = {
  margin: "22px 0 10px",
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
}

const poppins = "var(--font-poppins), 'Inter', system-ui, sans-serif"

const UNIT_TABS: { id: SodUnit; label: string }[] = [
  { id: "pallets", label: "Pallets" },
  { id: "rolls", label: "Rolls" },
  { id: "pieces", label: "Pieces" },
]

export function SodResults({ result, unit, onUnitChange, ctaHref, onCtaClick, ctaRef }: SodResultsProps) {
  const hero = formatSodUnit(result, unit)
  const greenCaution = result.inSeason && result.grassId !== "notsure"

  return (
    <div
      style={{
        borderRadius: 24,
        overflow: "hidden",
        background: "#fff",
        border: "1px solid #dcfce7",
        boxShadow: "0 12px 40px rgba(0,0,0,0.06)",
      }}
    >
      {/* gradient header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "16px 24px",
          background: "linear-gradient(90deg, #16a34a 0%, #059669 100%)",
          color: "#fff",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 16, fontWeight: 700 }}>
          <Layers aria-hidden="true" style={{ width: 19, height: 19 }} />
          Your sod order
        </span>
        <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.9 }}>{result.grassShort}</span>
      </div>

      <div style={{ padding: 24 }}>
        {/* hero metric */}
        <div
          style={{
            padding: 22,
            borderRadius: 18,
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontFamily: poppins,
              fontSize: 44,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: "-0.02em",
              color: "#14532d",
            }}
          >
            {hero.big}
          </div>
          <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600, color: "#166534" }}>{hero.sub}</div>
        </div>

        {/* unit reframe toggle */}
        <div
          role="group"
          aria-label="Show order in pallets, rolls, or pieces"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 6,
            marginTop: 12,
            padding: 5,
            borderRadius: 12,
            background: "#f3f4f6",
          }}
        >
          {UNIT_TABS.map((u) => {
            const active = unit === u.id
            return (
              <button
                key={u.id}
                type="button"
                aria-pressed={active}
                onClick={() => onUnitChange(u.id)}
                style={{
                  boxSizing: "border-box",
                  minHeight: 38,
                  padding: "0 8px",
                  border: "none",
                  borderRadius: 9,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 700,
                  color: active ? "#166534" : "#6b7280",
                  background: active ? "#fff" : "transparent",
                  boxShadow: active ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                  transition: "background-color 150ms ease, color 150ms ease, box-shadow 150ms ease",
                }}
              >
                {u.label}
              </button>
            )
          })}
        </div>

        {/* stat tiles */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
          <StatTile value={result.orderAreaSqFt.toLocaleString()} label={`sq ft to order (${result.wastePct}% waste allowance)`} />
          <StatTile value={result.weightLabel} label="approx. weight (plan the delivery)" />
        </div>

        {/* costs */}
        <p style={{ ...sectionLabel, color: "#15803d" }}>Estimated cost · {result.regionLabel}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <CostRow
            icon={<Package aria-hidden="true" style={{ flexShrink: 0, width: 18, height: 18, color: "#16a34a" }} />}
            label="Material only (DIY install)"
            value={result.material.label}
            tone="plain"
          />
          <CostRow
            icon={<HardHat aria-hidden="true" style={{ flexShrink: 0, width: 18, height: 18, color: "#16a34a" }} />}
            label="Professionally installed"
            value={result.installed.label}
            tone="green"
          />
        </div>

        {/* primary CTA — outbound Angi affiliate link since the 2026-07-18 pivot */}
        {ctaHref && (
          <>
            <a
              ref={ctaRef}
              href={ctaHref}
              target="_blank"
              rel="sponsored noopener noreferrer"
              onClick={onCtaClick}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 9,
                boxSizing: "border-box",
                width: "100%",
                minHeight: 56,
                marginTop: 14,
                padding: "0 24px",
                border: "none",
                borderRadius: 9999,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 16,
                fontWeight: 700,
                color: "#fff",
                textDecoration: "none",
                background: "linear-gradient(90deg, #16a34a 0%, #059669 100%)",
                boxShadow: "0 8px 20px -6px rgba(22,163,74,0.30)",
              }}
            >
              Get my sod install quote
              <ArrowRight aria-hidden="true" style={{ width: 18, height: 18 }} />
            </a>
            <p style={{ margin: "8px 0 0", fontSize: 12, textAlign: "center", color: "#6b7280" }}>
              Free quotes from local sod installers. No obligation.
            </p>
          </>
        )}

        {/* sod vs seed */}
        <p style={{ ...sectionLabel, color: "#6b7280" }}>Sod vs seed for this lawn</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ padding: 16, borderRadius: 16, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Zap aria-hidden="true" style={{ width: 16, height: 16, color: "#16a34a" }} />
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#166534" }}>
                Sod
              </span>
            </div>
            <div style={{ marginTop: 8, fontFamily: poppins, fontSize: 20, fontWeight: 800, color: "#14532d" }}>
              {result.sodMaterialLabel}
            </div>
            <div style={{ marginTop: 4, fontSize: 12.5, lineHeight: 1.5, color: "#166534" }}>
              Instant lawn, walkable in 2-3 weeks
            </div>
          </div>
          <div style={{ padding: 16, borderRadius: 16, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Sprout aria-hidden="true" style={{ width: 16, height: 16, color: "#6b7280" }} />
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#6b7280" }}>
                Seed
              </span>
            </div>
            <div style={{ marginTop: 8, fontFamily: poppins, fontSize: 20, fontWeight: 800, color: "#111827" }}>
              {result.seedLabel}
            </div>
            <div style={{ marginTop: 4, fontSize: 12.5, lineHeight: 1.5, color: "#6b7280" }}>
              6-8 weeks to establish, one season to mature
            </div>
          </div>
        </div>
        <Callout icon={<Info aria-hidden="true" style={{ flexShrink: 0, width: 16, height: 16, marginTop: 1, color: "#16a34a" }} />} bg="#f9fafb" border="#e5e7eb" color="#374151">
          {result.deltaNote}
        </Callout>

        {/* timing caution */}
        <Callout
          icon={
            <CalendarClock
              aria-hidden="true"
              style={{ flexShrink: 0, width: 16, height: 16, marginTop: 1, color: greenCaution ? "#16a34a" : "#d97706" }}
            />
          }
          bg={greenCaution ? "#f0fdf4" : "#fffbeb"}
          border={greenCaution ? "#bbf7d0" : "#fde68a"}
          color={greenCaution ? "#166534" : "#92400e"}
        >
          {result.timingCaution}
        </Callout>

        {/* prep steps */}
        <p style={{ ...sectionLabel, color: "#6b7280" }}>Before the pallets arrive</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {result.prepSteps.map((step, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
              <Check aria-hidden="true" style={{ flexShrink: 0, width: 15, height: 15, marginTop: 2, color: "#16a34a" }} />
              <span style={{ fontSize: 13.5, lineHeight: 1.5, color: "#374151" }}>{step}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function StatTile({ value, label }: { value: string; label: string }) {
  return (
    <div style={{ padding: 14, borderRadius: 14, background: "#f9fafb", border: "1px solid #e5e7eb", textAlign: "center" }}>
      <div style={{ fontFamily: poppins, fontSize: 22, fontWeight: 800, lineHeight: 1, color: "#111827" }}>{value}</div>
      <div style={{ marginTop: 5, fontSize: 12, fontWeight: 600, color: "#6b7280" }}>{label}</div>
    </div>
  )
}

function CostRow({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode
  label: string
  value: string
  tone: "plain" | "green"
}) {
  const green = tone === "green"
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 16px",
        borderRadius: 14,
        background: green ? "#f0fdf4" : "#fff",
        border: `1px solid ${green ? "#bbf7d0" : "#e5e7eb"}`,
      }}
    >
      {icon}
      <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: green ? "#166534" : "#374151" }}>{label}</span>
      <span style={{ fontSize: 16, fontWeight: 800, color: green ? "#14532d" : "#111827" }}>{value}</span>
    </div>
  )
}

function Callout({
  icon,
  bg,
  border,
  color,
  children,
}: {
  icon: React.ReactNode
  bg: string
  border: string
  color: string
  children: React.ReactNode
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        marginTop: 12,
        padding: "13px 15px",
        borderRadius: 13,
        background: bg,
        border: `1px solid ${border}`,
      }}
    >
      {icon}
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color }}>{children}</p>
    </div>
  )
}
